import { useState, useRef } from 'react';
import {
  Box,
  Heading,
  Card,
  CardBody,
  CardHeader,
  VStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Flex,
  Text,
  Code,
  useClipboard,
  HStack,
  Textarea,
  useColorModeValue,
  Icon,
  TableContainer,
  Alert,
  AlertIcon,
  Skeleton,
  SkeletonText,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faCopy,
  faCheck,
  faTrash,
  faGlobe,
  faCode,
  faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { sitesListAtom, sitesLoadingAtom, sitesErrorAtom } from '../state/sitesAtom';
import { userAtom } from '../state/userAtom';
import { useLicenseProtection } from '../hooks/useLicenseProtection';
import { sitesApi } from '../api/sites';
import SiteModal from '../components/sites/SiteModal';

function SitesPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const codeBg = useColorModeValue('gray.50', 'gray.900');
  const skeletonBg = useColorModeValue('gray.50', 'gray.700');
  const { isReadOnly, protectAction } = useLicenseProtection();
  const cancelRef = useRef();

  // Get sites from Recoil state (fetched in AppInitializer)
  const sitesFromApi = useRecoilValue(sitesListAtom);
  const setSitesList = useSetRecoilState(sitesListAtom);
  const sitesLoading = useRecoilValue(sitesLoadingAtom);
  const sitesError = useRecoilValue(sitesErrorAtom);
  const user = useRecoilValue(userAtom);
  const isAdmin = user?.role === 'admin';

  // Modal state
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState(null);

  // Handle delete site
  const handleDeleteClick = (site) => {
    setSiteToDelete(site);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!siteToDelete) return;

    setIsSubmitting(true);
    try {
      await sitesApi.delete(siteToDelete.id);
      setSitesList(prev => prev.filter(s => (s.site_id || s.id) !== siteToDelete.id));
      toast({
        title: 'Site deleted',
        description: `${siteToDelete.name} has been removed.`,
        status: 'success',
        duration: 3000,
      });
      onDeleteClose();
      setSiteToDelete(null);
    } catch (error) {
      toast({
        title: 'Failed to delete site',
        description: error.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle create site
  const handleCreateSite = async (data) => {
    setIsSubmitting(true);
    try {
      const response = await sitesApi.create(data);
      if (response.data) {
        // Add the new site to the list
        setSitesList(prev => [...(prev || []), response.data]);
        toast({
          title: 'Site created',
          description: `${data.name} has been added successfully.`,
          status: 'success',
          duration: 3000,
        });
        onCreateClose();
      }
    } catch (error) {
      toast({
        title: 'Failed to create site',
        description: error.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to format site data from API
  const formatSite = (s) => ({
    id: s.site_id || s.id,
    name: s.site_name || s.name,
    url: s.site_url || s.url || `https://${(s.site_name || s.name).toLowerCase().replace(/\s+/g, '')}.example.com`,
    uuid: s.site_uuid || s.uuid || `site-${s.site_id || s.id}`,
    apiKey: s.api_key || `wr_live_${(s.site_id || s.id)}...`,
    status: s.status || 'active',
    zones: s.zones || 0,
  });

  const handleZonesClick = (site) => {
    navigate(`/zones?site=${site.id}`);
  };

  const sitesList = sitesFromApi ? sitesFromApi.map(formatSite) : [];

  return (
    <Box>
      {/* Page Header */}
      <Box mb={{ base: 6, md: 8 }}>
        <Heading
          size={{ base: 'lg', md: 'xl' }}
          bgGradient="linear(to-r, brand.500, accent.500)"
          bgClip="text"
          fontWeight="700"
        >
          Sites
        </Heading>
        <Text color={textSecondary} mt={1}>
          Manage your registered websites and tracking codes
        </Text>
      </Box>

      <VStack spacing={6} align="stretch">
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardHeader pb={2}>
            <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
              <HStack spacing={2}>
                <Box p={2} bgGradient="linear(135deg, brand.500 0%, accent.500 100%)" borderRadius="lg">
                  <Icon color="white" boxSize={4}>
                    <FontAwesomeIcon icon={faGlobe} />
                  </Icon>
                </Box>
                <Heading size="md">Registered Sites</Heading>
              </HStack>
              <Button
                leftIcon={<FontAwesomeIcon icon={faPlus} />}
                colorScheme="brand"
                size="sm"
                borderRadius="lg"
                onClick={() => protectAction(onCreateOpen)}
                isDisabled={isReadOnly}
                title={isReadOnly ? 'License required to add sites' : undefined}
              >
                Add Site
              </Button>
            </Flex>
          </CardHeader>
          <CardBody pt={0}>
            {sitesError && (
              <Alert status="error" borderRadius="xl" mb={4}>
                <AlertIcon />
                {sitesError}
              </Alert>
            )}

            {sitesLoading ? (
              <>
                {/* Mobile Loading */}
                <VStack spacing={4} display={{ base: 'flex', md: 'none' }} align="stretch">
                  {[1, 2, 3].map((i) => (
                    <Box key={i} p={4} bg={skeletonBg} borderRadius="xl">
                      <Skeleton height="20px" mb={2} />
                      <Skeleton height="14px" width="60%" mb={3} />
                      <SkeletonText noOfLines={2} spacing={2} />
                    </Box>
                  ))}
                </VStack>
                {/* Desktop Loading */}
                <Box display={{ base: 'none', md: 'block' }}>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} height="50px" mb={2} borderRadius="md" />
                  ))}
                </Box>
              </>
            ) : sitesList.length > 0 ? (
              <>
                {/* Mobile Card View */}
                <VStack spacing={4} display={{ base: 'flex', md: 'none' }} align="stretch">
                  {sitesList.map((site) => (
                    <SiteCard
                      key={site.id}
                      site={site}
                      onZonesClick={handleZonesClick}
                      isReadOnly={isReadOnly}
                      onProtectedAction={protectAction}
                      onDelete={handleDeleteClick}
                      isAdmin={isAdmin}
                    />
                  ))}
                </VStack>

                {/* Desktop Table View */}
                <TableContainer display={{ base: 'none', md: 'block' }}>
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Site Name</Th>
                        <Th display={{ base: 'none', lg: 'table-cell' }}>URL</Th>
                        <Th>Site UUID</Th>
                        <Th isNumeric>Zones</Th>
                        <Th>Status</Th>
                        <Th width="100px"></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {sitesList.map((site) => (
                        <SiteRow
                          key={site.id}
                          site={site}
                          hoverBg={hoverBg}
                          textSecondary={textSecondary}
                          onZonesClick={handleZonesClick}
                          isReadOnly={isReadOnly}
                          onProtectedAction={protectAction}
                          onDelete={handleDeleteClick}
                          isAdmin={isAdmin}
                        />
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <Text color={textSecondary} textAlign="center" py={4}>
                No sites registered yet. Add your first site to get started.
              </Text>
            )}
          </CardBody>
        </Card>

        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardHeader pb={2}>
            <HStack spacing={2}>
              <Box p={2} bgGradient="linear(135deg, cyan.500 0%, blue.500 100%)" borderRadius="lg">
                <Icon color="white" boxSize={4}>
                  <FontAwesomeIcon icon={faCode} />
                </Icon>
              </Box>
              <Heading size="md">Tracking Code</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <Text fontSize="sm" color={textSecondary} mb={4}>
              Add this script to the footer of your website (before the closing &lt;/body&gt; tag):
            </Text>
            <TrackingCodeBlock codeBg={codeBg} />
          </CardBody>
        </Card>
      </VStack>

      {/* Create Site Modal */}
      <SiteModal
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        onSubmit={handleCreateSite}
        isLoading={isSubmitting}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Site
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete <strong>{siteToDelete?.name}</strong>? This will also delete all associated zones and tracking data. This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConfirm}
                ml={3}
                isLoading={isSubmitting}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

function SiteCard({ site, onZonesClick, isReadOnly, onProtectedAction, onDelete, isAdmin }) {
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const { hasCopied, onCopy } = useClipboard(site.uuid);

  // Generate site-specific tracking code with noscript fallback
  const serverUrl = window.location.origin.replace(':3058', ':3059');
  const trackingCode = `<!-- SF - ${site.name} -->
<script src="${serverUrl}/tracker.js?site=${site.uuid}"></script>
<noscript><img src="${serverUrl}/api/track/pixel.gif?site=${site.uuid}&type=pageview" alt="" style="position:absolute;visibility:hidden;width:1px;height:1px;"></noscript>`;
  const { hasCopied: hasCodeCopied, onCopy: onCodeCopy } = useClipboard(trackingCode);

  return (
    <Box p={4} bg={cardBg} borderRadius="xl" border="1px solid" borderColor={borderColor}>
      <Flex justify="space-between" align="start" mb={3}>
        <Box>
          <Text fontWeight="600">{site.name}</Text>
          <Text fontSize="sm" color={textSecondary}>{site.url}</Text>
        </Box>
        <HStack spacing={2}>
          <Badge
            colorScheme="purple"
            borderRadius="full"
            cursor={site.zones > 0 ? 'pointer' : 'default'}
            _hover={site.zones > 0 ? { opacity: 0.8 } : {}}
            onClick={() => site.zones > 0 && onZonesClick && onZonesClick(site)}
          >
            {site.zones} zones
          </Badge>
          <Badge colorScheme={site.status === 'active' ? 'green' : 'gray'} borderRadius="full">
            {site.status}
          </Badge>
        </HStack>
      </Flex>
      <HStack spacing={2} mb={2}>
        <Code fontSize="xs" borderRadius="lg" px={2} py={1}>{site.uuid.substring(0, 16)}...</Code>
        <IconButton
          icon={<FontAwesomeIcon icon={hasCopied ? faCheck : faCopy} />}
          size="xs"
          variant="ghost"
          onClick={onCopy}
          aria-label="Copy UUID"
          borderRadius="lg"
        />
      </HStack>
      <HStack mt={3} spacing={2}>
        <Button
          leftIcon={<FontAwesomeIcon icon={hasCodeCopied ? faCheck : faCode} />}
          size="xs"
          colorScheme={hasCodeCopied ? 'green' : 'brand'}
          onClick={onCodeCopy}
          borderRadius="lg"
        >
          {hasCodeCopied ? 'Copied!' : 'Copy Tracking Code'}
        </Button>
        {isAdmin && (
          <IconButton
            icon={<FontAwesomeIcon icon={faTrash} />}
            size="xs"
            variant="ghost"
            colorScheme={isReadOnly ? 'gray' : 'red'}
            aria-label="Delete site"
            borderRadius="lg"
            onClick={() => onProtectedAction && onProtectedAction(() => onDelete(site))}
            isDisabled={isReadOnly}
          />
        )}
      </HStack>
    </Box>
  );
}

function SiteRow({ site, hoverBg, textSecondary, onZonesClick, isReadOnly, onProtectedAction, onDelete, isAdmin }) {
  const { hasCopied, onCopy } = useClipboard(site.uuid);

  // Generate site-specific tracking code with noscript fallback
  const serverUrl = window.location.origin.replace(':3058', ':3059');
  const trackingCode = `<!-- SF - ${site.name} -->
<script src="${serverUrl}/tracker.js?site=${site.uuid}"></script>
<noscript><img src="${serverUrl}/api/track/pixel.gif?site=${site.uuid}&type=pageview" alt="" style="position:absolute;visibility:hidden;width:1px;height:1px;"></noscript>`;
  const { hasCopied: hasCodeCopied, onCopy: onCodeCopy } = useClipboard(trackingCode);

  return (
    <Tr _hover={{ bg: hoverBg }} transition="all 0.2s">
      <Td fontWeight="600">{site.name}</Td>
      <Td display={{ base: 'none', lg: 'table-cell' }}>
        <Text fontSize="sm" color={textSecondary}>{site.url}</Text>
      </Td>
      <Td>
        <HStack>
          <Code fontSize="xs" borderRadius="lg">{site.uuid.substring(0, 8)}...</Code>
          <IconButton
            icon={<FontAwesomeIcon icon={hasCopied ? faCheck : faCopy} />}
            size="xs"
            variant="ghost"
            onClick={onCopy}
            aria-label="Copy UUID"
            title="Copy Site UUID"
            borderRadius="lg"
          />
        </HStack>
      </Td>
      <Td isNumeric>
        <Text
          fontWeight="500"
          color={site.zones > 0 ? 'purple.500' : undefined}
          cursor={site.zones > 0 ? 'pointer' : 'default'}
          _hover={site.zones > 0 ? { textDecoration: 'underline' } : {}}
          onClick={() => site.zones > 0 && onZonesClick && onZonesClick(site)}
        >
          {site.zones}
        </Text>
      </Td>
      <Td>
        <Badge colorScheme={site.status === 'active' ? 'green' : 'gray'} borderRadius="full">
          {site.status}
        </Badge>
      </Td>
      <Td>
        <HStack>
          <IconButton
            icon={<FontAwesomeIcon icon={hasCodeCopied ? faCheck : faCode} />}
            size="xs"
            variant="ghost"
            colorScheme={hasCodeCopied ? 'green' : 'brand'}
            onClick={onCodeCopy}
            aria-label="Copy tracking code"
            title="Copy Tracking Code"
            borderRadius="lg"
          />
          {isAdmin && (
            <IconButton
              icon={<FontAwesomeIcon icon={faTrash} />}
              size="xs"
              variant="ghost"
              colorScheme={isReadOnly ? 'gray' : 'red'}
              aria-label="Delete site"
              borderRadius="lg"
              onClick={() => onProtectedAction && onProtectedAction(() => onDelete(site))}
              isDisabled={isReadOnly}
            />
          )}
        </HStack>
      </Td>
    </Tr>
  );
}

function TrackingCodeBlock({ codeBg }) {
  const serverUrl = window.location.origin.replace(':3058', ':3059'); // Backend server
  const trackingCode = `<!-- SF -->
<script src="${serverUrl}/tracker.js?site=YOUR_SITE_UUID"></script>
<noscript><img src="${serverUrl}/api/track/pixel.gif?site=YOUR_SITE_UUID&type=pageview" alt="" style="position:absolute;visibility:hidden;width:1px;height:1px;"></noscript>`;

  const { hasCopied, onCopy } = useClipboard(trackingCode);

  return (
    <Box>
      <Textarea
        value={trackingCode}
        readOnly
        fontFamily="mono"
        fontSize="sm"
        rows={4}
        mb={3}
        bg={codeBg}
        borderRadius="xl"
      />
      <Text fontSize="sm" color="gray.500" mb={3}>
        Replace YOUR_SITE_UUID with the Site UUID from your registered site above.
        Use the "Copy Tracking Code" button on each site for a ready-to-use snippet.
      </Text>
      <HStack spacing={3}>
        <Button
          leftIcon={<FontAwesomeIcon icon={hasCopied ? faCheck : faCopy} />}
          onClick={onCopy}
          colorScheme={hasCopied ? 'green' : 'brand'}
          size="sm"
          borderRadius="lg"
        >
          {hasCopied ? 'Copied!' : 'Copy Code'}
        </Button>
        <Button
          leftIcon={<FontAwesomeIcon icon={faExternalLinkAlt} />}
          onClick={() => window.open(`${serverUrl}/test/`, '_blank')}
          variant="outline"
          colorScheme="brand"
          size="sm"
          borderRadius="lg"
        >
          Open Test Page
        </Button>
      </HStack>
    </Box>
  );
}

export default SitesPage;
